export enum PaymentStatus {
  INITIATED = 'INITIATED',
}
export enum DiscountType {
  // e.g. PERCENT = "PERCENT", AMOUNT = "AMOUNT"
}
export enum PaymentType {
  // e.g. CARD = "CARD", WALLET = "WALLET"
}

// Minimal related references.
// Expand these to the full shapes you actually return.
export type FishRef = { id: string };
export type SubscriptionRef = { id: string } | null;
export type PaymentIntentRef = { id: string };
export type WalletPaymentRef = { id: string };
export type EventBookingEventRef = { id: string } | null;
export type TicketedEventTicketRef = { id: string };

// -------------------------------
// 1) Wire/API shape (safe defaults)
// -------------------------------
export interface PurchaseDTO {
  id: string;

  fishId: string;
  fish?: FishRef; // include if your API expands relations
  status: PaymentStatus;

  price: string;
  originalPrice?: string | null;
  discountAmount?: string | null;
  discountType?: DiscountType | null;

  productDescription: string;

  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;

  toastPaymentIntents?: PaymentIntentRef[]; // if included
  subscriptionId?: string | null;
  subscription?: SubscriptionRef; // if included
  walletPayments?: WalletPaymentRef[]; // if included

  order?: unknown | null;

  eventId?: string | null;
  event?: EventBookingEventRef; // if included
  paymentType?: PaymentType | null;

  confirmationNumber?: string | null;
  restaurantDetails?: unknown | null;

  ticketedEventTickets?: TicketedEventTicketRef[]; // if included
}

// -----------------------------------------
// 2) Parsed/app shape for convenient usage
//    (numbers and dates parsed)
// -----------------------------------------
export interface Purchase {
  id: string;

  fishId: string;
  fish?: FishRef;
  status: PaymentStatus;

  price: number; // parsed from string
  originalPrice?: number | null; // parsed
  discountAmount?: number | null;
  discountType?: DiscountType | null;

  productDescription: string;

  createdAt: Date; // parsed from ISO string
  updatedAt: Date;
  deletedAt?: Date | null;

  toastPaymentIntents?: PaymentIntentRef[];
  subscriptionId?: string | null;
  subscription?: SubscriptionRef;
  walletPayments?: WalletPaymentRef[];

  order?: unknown | null;

  eventId?: string | null;
  event?: EventBookingEventRef;
  paymentType?: PaymentType | null;

  confirmationNumber?: string | null;
  restaurantDetails?: unknown | null;

  ticketedEventTickets?: TicketedEventTicketRef[];
}

const allTimeSpend = (purchases: Purchase[]): number => {
  return purchases.reduce((total, purchase) => total + purchase.price, 0);
};

const monthToDateSpend = (purchases: Purchase[]): number => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  return purchases
    .filter((purchase) => purchase.createdAt >= startOfMonth)
    .reduce((total, purchase) => total + purchase.price, 0);
};

const yearToDateSpend = (purchases: Purchase[]): number => {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);

  return purchases
    .filter((purchase) => purchase.createdAt >= startOfYear)
    .reduce((total, purchase) => total + purchase.price, 0);
};

/**
 * Formats a phone number into "+1 512 639 5475" style.
 * - Normalizes punctuation/spaces.
 * - Adds +1 if missing and number looks like a US number (10 digits).
 * - Handles any country code.
 */
export function formatPhone(input: string): string {
  if (!input) return '';

  // Strip all characters except digits and +
  let normalized = input.replace(/[^\d+]/g, '');

  // Ensure only the leading + is kept if multiple appear
  if (normalized.startsWith('++')) normalized = normalized.replace(/^\++/, '+');

  // Handle missing +1 for US numbers (assume if 10 digits only)
  const digitsOnly = normalized.replace(/\D/g, '');
  if (!normalized.startsWith('+') && digitsOnly.length === 10) {
    normalized = `+1${digitsOnly}`;
  }

  // Match pattern: +1XXXXXXXXXX (US numbers)
  const matchUS = normalized.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (matchUS) {
    const [, area, mid, last] = matchUS;
    return `+1 ${area} ${mid} ${last}`;
  }

  // For other international numbers, format as +CC XXX XXX XXXX progressively
  const intlMatch = normalized.match(/^\+(\d{1,3})(\d{3})(\d{3})(\d{0,4})$/);
  if (intlMatch) {
    const [, country, part1, part2, part3] = intlMatch;
    const tail = part3 ? ` ${part3}` : '';
    return `+${country} ${part1} ${part2}${tail}`;
  }

  // Fallback: space every 3–4 digits for readability
  const generic = normalized.replace(/(\d{3,4})(?=\d)/g, '$1 ');
  return generic.trim();
}

export { allTimeSpend, monthToDateSpend, yearToDateSpend };
